/**
 * Generic "center FAB override" event bus for BottomNav.
 *
 * Any dialog/component that wants to temporarily replace the BottomNav center
 * button (e.g. with a mic toggle) dispatches an override via
 * `setCenterActionOverride(...)` and clears it with `clearCenterActionOverride()`.
 *
 * BottomNav listens for these events and renders the override icon + click
 * handler instead of the default AI Check-in link while an override is active.
 *
 * The pattern is intentionally generic so we never hardcode per-route logic
 * inside BottomNav.
 */

// ---------------------------------------------------------------------------
// Event names
// ---------------------------------------------------------------------------

/** Dispatched by a dialog to install/update/clear a center override. */
export const CENTER_ACTION_OVERRIDE_EVENT =
  "floline:center-action:override" as const;

/** Dispatched by BottomNav when the overridden center FAB is tapped. */
export const CENTER_ACTION_TAP_EVENT =
  "floline:center-action:tap" as const;

// ---------------------------------------------------------------------------
// Payload types
// ---------------------------------------------------------------------------

export type CenterActionIcon = "mic" | "stop" | "loading";

export interface CenterActionOverride {
  /** Which icon to show. `null` means "clear the override". */
  icon: CenterActionIcon | null;
  /** Whether the override button should appear disabled. */
  disabled?: boolean;
  /** True when actively recording (drives red styling + pulse rings). */
  isRecording?: boolean;
  /** Accessible label for the button. */
  ariaLabel?: string;
}

// ---------------------------------------------------------------------------
// Dispatch helpers
// ---------------------------------------------------------------------------

export function setCenterActionOverride(override: CenterActionOverride) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<CenterActionOverride>(CENTER_ACTION_OVERRIDE_EVENT, {
      detail: override,
    }),
  );
}

export function clearCenterActionOverride() {
  setCenterActionOverride({ icon: null });
}

export function dispatchCenterActionTap() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CENTER_ACTION_TAP_EVENT));
}
